import { inject, InjectionToken } from '@angular/core';
import { combineLatest, filter, map, Observable, shareReplay, startWith, switchMap } from 'rxjs';
import { SideNavigationContainer } from '@vendasta/atlas';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AccountGroupService } from '../account-group';
import { FeatureFlagService } from '../core/feature-flag.service';
import { BusinessNavDataService } from '@vendasta/business-nav';
import { NavigationData } from '../navigation/navigation.interface';

/**
 * The URL to the external app associated with the current route.
 */
export const LaunchAppUrlToken = new InjectionToken<Observable<string>>(
  'the url to the external app associated with the current route',
  {
    factory: () => {
      const accountGroupService = inject(AccountGroupService);
      const featureFlagService = inject(FeatureFlagService);
      const navDataService = inject(BusinessNavDataService);
      const router = inject(Router);
      const route = inject(ActivatedRoute);

      const routeData$: Observable<NavigationData> = router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith(null),
        map(() => mergeData(route)),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

      const launchAppUrlFromRouteData$ = routeData$.pipe(map((data) => data.appUrl));

      const launchAppUrlForCurrentPage$: Observable<string | null> = combineLatest([
        navDataService.navigationItems$.pipe(
          map((navItems): SideNavigationContainer[] => {
            return navItems
              .filter((navItem) => Boolean(navItem.sideNavigationContainer))
              .map((navItem) => navItem.sideNavigationContainer);
          }),
        ),
        router.events.pipe(
          filter((event) => event instanceof NavigationEnd),
          map((event: NavigationEnd) => event.url),
          startWith(router.routerState.snapshot.url),
        ),
      ]).pipe(
        map(([navContainers, currentPath]) => {
          const navContainerForCurrentPage = navContainers.find((navContainer) => {
            navContainer.sideNavigationItems.some((navItem) => {
              return navItem.sideNavigationLink.path === currentPath;
            });
          });
          return navContainerForCurrentPage?.url || null;
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

      const featureFlagsEnabled$ = accountGroupService.currentAccountGroup$.pipe(
        switchMap((ag) => featureFlagService.checkFeatureFlag(ag.partnerId, ag.marketId, 'bc_single_nav')),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

      return combineLatest([
        launchAppUrlFromRouteData$.pipe(startWith('')),
        launchAppUrlForCurrentPage$.pipe(startWith('')),
        featureFlagsEnabled$,
      ]).pipe(
        map(([appUrlFromRouteData, launchAppUrlForCurrentPage, featureFlagEnabled]) => {
          if (appUrlFromRouteData) return appUrlFromRouteData;
          if (featureFlagEnabled && launchAppUrlForCurrentPage) return appUrlFromRouteData;
          return '';
        }),
        startWith(''),
      );
    },
  },
);

function mergeData(route: ActivatedRoute, data: NavigationData = {}): NavigationData {
  if (route.snapshot?.data) {
    const { appUrl, injectedPageContent, pageOptions, breadcrumbs } = route.snapshot.data;

    data.pageOptions = { ...data.pageOptions, ...pageOptions };
    data.appUrl = appUrl;
    if (breadcrumbs) data.breadcrumbs = breadcrumbs;

    data.injectedPageContent = { ...data.injectedPageContent, ...injectedPageContent };
  }

  route.children.forEach((childRoute) => mergeData(childRoute, data));

  return data;
}
